/**
 * useSeaMonsters — farver de frilagte havuhyrer efter temaet.
 *
 * Filerne fra scripts/build-sea-monsters.mjs er gråtoner på hvidt papir.
 * Her bliver hver pixel til alfa = 255 − luminans, så papiret forsvinder og
 * kun stregen står tilbage; farven lægges ovenpå, varm pergament i mørkt
 * tema og kold blæk i lyst.
 *
 * `wideView` er porten: uhyrerne er tonet helt ud over zoom 8, og et
 * bydeeplink skal ikke betale 207 KiB for fire billeder ingen kan se.
 *
 * Tre ting her er der af hensyn til første paint og ikke af smag. Se
 * docs/HAVUHYRER.md for hvad der blev målt.
 *
 * Forbruger: App.tsx' render (image-sources over havet).
 */
import { useEffect, useRef, useState } from "react";
import type { ThemeMode } from "../types/index.ts";
import { SEA_MONSTERS } from "../constants/seaMonsters.ts";
import { warnFetch } from "../utils/logErr.ts";

/** Under dette bliver en pixel helt gennemsigtig; fjerner webp-slør. */
const KNAE = 10;

function farvelaeg(img: HTMLImageElement, isDark: boolean): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = img.width; c.height = img.height;
  const ctx = c.getContext("2d")!;
  ctx.drawImage(img, 0, 0);
  const d = ctx.getImageData(0, 0, c.width, c.height);
  for (let i = 0; i < d.data.length; i += 4) {
    const r = d.data[i], g = d.data[i + 1], b = d.data[i + 2];
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    const raa = 255 - lum;
    const baseAlpha = raa <= KNAE ? 0 : raa;
    if (isDark) {
      d.data[i]     = Math.round(220 + (255 - r) * 0.13);
      d.data[i + 1] = Math.round(225 + (255 - g) * 0.10);
      d.data[i + 2] = Math.round(235 + (255 - b) * 0.06);
      d.data[i + 3] = Math.min(255, Math.round(baseAlpha * 1.6));
    } else {
      d.data[i]     = Math.round(r * 0.4);
      d.data[i + 1] = Math.round(g * 0.5);
      d.data[i + 2] = Math.round(Math.min(255, b * 0.8 + 80));
      d.data[i + 3] = Math.round(baseAlpha);
    }
  }
  ctx.putImageData(d, 0, 0);
  return c;
}

function hentBillede(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function somObjektUrl(c: HTMLCanvasElement): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      c.toBlob((b) => resolve(b ? URL.createObjectURL(b) : null), "image/png");
    } catch { resolve(null); }
  });
}

/** requestIdleCallback findes ikke i Safari; der venter vi et billede. */
function naarDerErTid(fn: () => void): () => void {
  const w = window as unknown as {
    requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(fn, { timeout: 4000 });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(fn, 1200);
  return () => window.clearTimeout(id);
}

export function useSeaMonsters(resolved: ThemeMode, wideView: boolean): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  /**
   * De object-URL'er der er i brug lige nu. De kaldes først tilbage når noget
   * andet er sat i stedet, aldrig i effektens oprydning: et temaskift lægger
   * også et nyt kortstilart på, og react-map-gl sætter da alle image-sources
   * op igen. Hvis oprydningen havde kaldt dem tilbage først, ville MapLibre
   * hente fire døde blob:-URL'er og uhyrerne forsvinde indtil det nye sæt var
   * farvet færdigt.
   */
  const ibrugRef = useRef<string[]>([]);

  useEffect(() => {
    const lægFrem = (nye: Record<string, string>) => {
      const gamle = ibrugRef.current;
      ibrugRef.current = Object.values(nye);
      setUrls(nye);
      // Et billede efter skiftet, så MapLibre har nået at læse de nye.
      requestAnimationFrame(() => { for (const u of gamle) URL.revokeObjectURL(u); });
    };

    if (!wideView) {
      // Porten er lukket. Lagene skal ud af stilarten, ellers står fire
      // usynlige raster-lag tilbage med URL'er ingen kan hente mere.
      if (ibrugRef.current.length) lægFrem({});
      return;
    }

    let levende = true;

    const stop = naarDerErTid(async () => {
      const isDark = resolved === "dark";
      // allSettled og try/catch pr. dyr: farvelaeg kan kaste (et nægtet
      // 2d-kontekst, et tilsmudset canvas), og ét kast måtte ikke tage de
      // tre andre med sig ned i en ufanget rejection.
      const par = await Promise.all(SEA_MONSTERS.map(async (uhyre) => {
        try {
          const img = await hentBillede(uhyre.file);
          if (!img || !levende) return null;
          const url = await somObjektUrl(farvelaeg(img, isDark));
          return url ? ([uhyre.id, url] as const) : null;
        } catch (e) {
          warnFetch(`sea monster ${uhyre.id}`, e);
          return null;
        }
      }));
      if (!levende) {
        for (const p of par) if (p) URL.revokeObjectURL(p[1]);
        return;
      }
      // Ét setState for alle fire. Fire hver for sig gav fire gengivelser af
      // hele App.tsx midt i kortets første paint.
      const nye: Record<string, string> = {};
      for (const p of par) if (p) nye[p[0]] = p[1];
      if (Object.keys(nye).length) lægFrem(nye);
    });

    return () => { levende = false; stop(); };
  }, [resolved, wideView]);

  return urls;
}
